import { useMemo, useState, type FormEvent } from 'react'
import useSWR from 'swr'
import { useDebounce } from '../hooks'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { disableUser, enableUser, fetchUsers, inviteUser, setUserRoles, type CmsUser, type InvitedUser } from '../services/api'
import { ROLE_LABELS, ROLES, type Role } from '../auth/roles'
import { Badge, Button, Modal, useSpeedbump } from '../components'
import type { BadgeVariant } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import formStyles from '../styles/forms.module.scss'
import styles from './UsersList.module.scss'

// Cognito UserStatus values a CMS admin will see. An invited person stays
// FORCE_CHANGE_PASSWORD until their first sign-in.
const STATUS_LABEL: Record<string, { label: string; variant: BadgeVariant }> = {
  CONFIRMED: { label: 'Active', variant: 'success' },
  FORCE_CHANGE_PASSWORD: { label: 'Invited', variant: 'warning' },
  RESET_REQUIRED: { label: 'Reset required', variant: 'warning' },
}

const statusBadge = (user: CmsUser) =>
  !user.enabled
    ? { label: 'Disabled', variant: 'neutral' as BadgeVariant }
    : STATUS_LABEL[user.status] ?? { label: user.status, variant: 'secondary' as BadgeVariant }

const displayName = (user: CmsUser) => user.email ?? user.username

export default function UsersList() {
  const { data, isLoading, error, mutate } = useSWR('users', fetchUsers, { revalidateOnFocus: false })
  const { user: me, refreshRoles } = useAuth()
  const { toast } = useToast()
  const confirm = useSpeedbump()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 300)
  // Per user, so a save on one row cannot unlock another row mid-save.
  const [savingUsernames, setSavingUsernames] = useState<Set<string>>(new Set())
  const [inviteOpen, setInviteOpen] = useState(false)

  const users = useMemo(() => data ?? [], [data])
  const noRoleCount = users.filter((u) => u.enabled && u.roles.length === 0).length

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase()
    return users.filter((u) => !query || displayName(u).toLowerCase().includes(query))
  }, [users, debouncedSearch])

  const replaceUser = (updated: CmsUser) =>
    mutate(
      (current) => current?.map((u) => (u.username === updated.username ? updated : u)),
      { revalidate: false }
    )

  const run = async (user: CmsUser, action: () => Promise<CmsUser>, failure: string) => {
    setSavingUsernames((prev) => new Set(prev).add(user.username))
    try {
      const updated = await action()
      await replaceUser(updated)
      return updated
    } catch (err) {
      toast(err instanceof Error ? err.message : failure, 'error')
      return null
    } finally {
      setSavingUsernames((prev) => {
        const next = new Set(prev)
        next.delete(user.username)
        return next
      })
    }
  }

  const toggleRole = async (user: CmsUser, role: Role) => {
    const roles = user.roles.includes(role) ? user.roles.filter((r) => r !== role) : [...user.roles, role]
    const updated = await run(user, () => setUserRoles(user.username, roles), 'Failed to update roles')
    if (!updated) return
    if (user.username === me?.username) {
      // Pick up the new groups now instead of at the next token refresh.
      try {
        await refreshRoles()
      } catch {
        toast('Roles saved. Reload the page to see your new access.', 'warning')
      }
    } else {
      // An added role works on their next refused request (the CMS refreshes on
      // a 403); a removed one ends at their next token refresh.
      toast(`Roles saved. A removed role stops working for ${displayName(user)} within 15 minutes.`, 'success')
    }
  }

  const toggleEnabled = async (user: CmsUser) => {
    if (user.enabled) {
      const ok = await confirm({
        title: 'Disable user',
        confirmLabel: 'Disable',
        confirmVariant: 'danger',
        message: `${displayName(user)} will be signed out and cannot sign in again until enabled. Their current session can last up to 15 more minutes.`,
      })
      if (!ok) return
      await run(user, () => disableUser(user.username), 'Failed to disable user')
    } else {
      await run(user, () => enableUser(user.username), 'Failed to enable user')
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Couldn’t load users</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => void mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button onClick={() => setInviteOpen(true)}>Invite user</Button>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Users</h1>
        <p>Give CMS users their roles. Admin includes Live Ops and Content.</p>
      </div>

      <p className={styles.summary}>
        {`${users.length} users`}
        {noRoleCount > 0 && ` · ${noRoleCount} active with no role`}
      </p>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users by email…"
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading…</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No users match your search</h3>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listRow}`}>
            <div>User</div>
            {ROLES.map((role) => (
              <div key={role}>{ROLE_LABELS[role]}</div>
            ))}
            <div>Status</div>
            <div />
          </div>
          <div className={listTableStyles.tableBody}>
            {filtered.map((user) => {
              const isMe = user.username === me?.username
              const saving = savingUsernames.has(user.username)
              const badge = statusBadge(user)
              return (
                <div key={user.username} className={`${listTableStyles.tableRow} ${styles.listRow}`}>
                  <div className={listTableStyles.cellPrimary}>
                    <p className={listTableStyles.primaryText}>
                      {displayName(user)}
                      {isMe && <span className={styles.you}> (you)</span>}
                    </p>
                    {user.roles.length === 0 && user.enabled && (
                      <p className={listTableStyles.secondaryText}>No role: this user cannot use the CMS once role gates are on.</p>
                    )}
                  </div>
                  {ROLES.map((role) => (
                    <div key={role} className={listTableStyles.cellText}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={user.roles.includes(role)}
                        // You cannot remove your own admin role (the API refuses it too).
                        disabled={saving || (isMe && role === 'admin')}
                        aria-label={`${ROLE_LABELS[role]} role for ${displayName(user)}`}
                        onChange={() => void toggleRole(user, role)}
                      />
                    </div>
                  ))}
                  <div className={listTableStyles.cellBadges}>
                    <Badge variant={badge.variant} size="small">
                      {badge.label}
                    </Badge>
                  </div>
                  <div className={listTableStyles.cellActions}>
                    {!isMe && (
                      <Button
                        variant={user.enabled ? 'danger' : 'secondary'}
                        size="small"
                        disabled={saving}
                        onClick={() => void toggleEnabled(user)}
                      >
                        {user.enabled ? 'Disable' : 'Enable'}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      <InviteUserModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(created) => {
          setInviteOpen(false)
          const { failed_roles: failedRoles, ...user } = created
          void mutate((current) => [...(current ?? []), user], { revalidate: false })
          if (failedRoles.length) {
            toast(
              `Invitation sent to ${displayName(user)}, but these roles were not added: ${failedRoles
                .map((r) => ROLE_LABELS[r])
                .join(', ')}. Set them in the list.`,
              'warning'
            )
          } else {
            toast(`Invitation sent to ${displayName(user)}.`, 'success')
          }
        }}
      />
    </div>
  )
}

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onInvited: (user: InvitedUser) => void
}

function InviteUserModal({ isOpen, onClose, onInvited }: InviteUserModalProps) {
  const [email, setEmail] = useState('')
  const [roles, setRoles] = useState<Role[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setEmail('')
    setRoles([])
    setError(null)
    onClose()
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const created = await inviteUser(email.trim(), roles)
      setEmail('')
      setRoles([])
      onInvited(created)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title="Invite user" size="small">
      <form onSubmit={(e) => void submit(e)} className={styles.inviteForm}>
        <div className={formStyles.fieldGroup}>
          <label htmlFor="inviteEmail">Email</label>
          <input
            id="inviteEmail"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={formStyles.input}
          />
        </div>
        <fieldset className={styles.roleFieldset}>
          <legend>Roles</legend>
          {ROLES.map((role) => (
            <label key={role} className={formStyles.checkboxLabel}>
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={() =>
                  setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
                }
              />
              {ROLE_LABELS[role]}
            </label>
          ))}
        </fieldset>
        <p className={formStyles.hint}>
          Cognito emails a temporary password. At first sign-in the user sets a password and enrols an
          authenticator app.
        </p>
        {error && <p className={formStyles.errorText}>{error}</p>}
        <div className={formStyles.formActions}>
          <Button type="submit" disabled={submitting || !email.trim()}>
            {submitting ? 'Sending…' : 'Send invitation'}
          </Button>
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  )
}
